import axios from "axios";
import Swal from "sweetalert2";

const api = axios.create({
  baseURL: process.env.VUE_APP_BACKEND_API + "/api/",
});

api.defaults.headers.common["Accept"] = "application/json";

let sessionAlertShown = false;
let isLoggingIn = false;
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

function isRecentLogin() {
  const loginTime = sessionStorage.getItem('loginTime');
  if (!loginTime) return false;
  return Date.now() - parseInt(loginTime) < 5000;
}

api.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;
    const url = error.config && error.config.url ? error.config.url : '';
    const isAuthEndpoint = url.includes('auth/login') || url.includes('auth/logout') || url.includes('auth/refresh');

    if (error.response && error.response.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const store = (await import('./store')).default;
        
        const newTokenData = await store.dispatch('auth/refreshAccessToken');
        const newToken = `${newTokenData.token_type} ${newTokenData.access_token}`;
        
        processQueue(null, newToken);
        
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
        isRefreshing = false;
        
        return api(originalRequest);
        
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        
        const shouldShowSessionExpired = refreshError.response && 
          (refreshError.response.status === 401 || refreshError.response.status === 403);
        
        if (shouldShowSessionExpired && !sessionAlertShown && !isLoggingIn && !isRecentLogin()) {
          sessionAlertShown = true;
          
          const currentPath = window.location.pathname;
          if (currentPath === '/login' || currentPath === '/') {
            sessionAlertShown = false;
            return Promise.reject(error);
          }
          
          const store = (await import('./store')).default;
          store.commit('auth/destroyAuth');
          
          Swal.fire({
            title: "Session Expired",
            text: "Your session has expired. You will be redirected to the login page.",
            icon: "warning",
            confirmButtonText: "Go to Login",
            allowOutsideClick: true,
            allowEscapeKey: true,
            timer: 5000,
            timerProgressBar: true
          }).then(function () {
            sessionAlertShown = false;
            window.location.href = '/login';
          });
        }
        
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

api.setLoggingIn = function(state) {
  isLoggingIn = state;
  if (state) {
    sessionStorage.setItem('loginTime', Date.now().toString());
  }
};

api.resetSessionAlert = function() {
  sessionAlertShown = false;
};

export default api;